import infoIcon from 'assets/images/icons/info.png';
import { useTranslation } from 'translation/useTranslation';
import sanitize from 'helpers/sanitize';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { empty, emptyHtml, errorMessage, fileDownloadResponse, formatNumberTo, getTextFromHtml } from 'helpers/common';
import EditorInput from 'components/ui/EditorInput';
import ExpandableHtml from 'components/ui/ExpandableHtml';
import { ReactComponent as AddCommentIcon } from 'assets/images/icons/add-comment.svg';
import { ReactComponent as EditCommentIcon } from 'assets/images/icons/edit-comment.svg';
import { ReactComponent as ViewCommentIcon } from 'assets/images/icons/view-comment.svg';
import { useLocation } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { getGeneralSettings } from 'store/generalSettingsSlice';
import { useSelector } from 'react-redux';
import { getStateEmployeeSettings } from 'store/stateEmployeeSlice';
import InterviewMode from 'components/BalanceScorecard/InterviewMode';
import { api } from 'helpers/axios';
import * as Sentry from "@sentry/react";
import SettingName from './SettingName';
import ErrorAlert from 'components/ui/ErrorAlert';
import LineClamp from 'components/ui/LineClamp';
import { Button } from "components/ui";

function V3SystematicObectiveRow({ isLast, data, settings, balancedScorecardDetails, handleLevelChange, readOnlyMode, levels, extremeLevels, maxSkippedCriteria = 0, maxSkippedCriteriaFull = false, errorField = null }) {
  const [showComment, setShowComment] = useState(false);

  const [isDetailOpened, setIsDetailOpened] = useState(false);

  const savedSystematicObjective = useMemo(() => {
    if (balancedScorecardDetails?.agreement_data?.systematicObjectives && Array.isArray(balancedScorecardDetails?.agreement_data?.systematicObjectives)) {
      const filtered = balancedScorecardDetails.agreement_data.systematicObjectives.filter(o => o.name === data.systematicObjectiveGridName);

      if (filtered.length > 0) {
        let agreed = filtered[0].agreed;

        if (agreed) return { ...filtered[0] };
      }
    }
    return null;
  }, [balancedScorecardDetails]);

  const isMandatory = useMemo(() => {
    return (settings?.systematicMandatoryCommentForExtremeRating === true && settings?.systematicMandatoryCommentForExtremeRatingType === 'based_on_single_objective' && (savedSystematicObjective?.level && extremeLevels.systematic.includes(savedSystematicObjective?.level))) ||
      savedSystematicObjective?.skipped;
  }, [settings, savedSystematicObjective, extremeLevels]);

  const getDescription = () => {
    const arr = [];

    const desc = data?.systematicObjectiveGridDescription;

    if (!empty(desc)) {
      const splitted = desc.split(';;');
      for (let i = 0; i < splitted.length; i++) {
        const item = splitted[i];
        const sentence = item.split(':');
        if (sentence.length === 2) {
          arr.push(<li key={i}><span className="font-weight-bold">{sentence[0]}</span>:<div>{sentence[1]}</div></li>);
        } else {
          arr.push(<li key={i}>{item}</li>);
        }
      }
    }

    return arr;
  };

  const handleSetSkipped = (e, skipped) => {
    if (readOnlyMode) return;

    e?.currentTarget?.blur();
    e?.target?.blur();
    handleLevelChange(skipped, savedSystematicObjective, 'skipped');
  };

  return savedSystematicObjective && <>
    <tr className={`${showComment || isDetailOpened ? 'has-comment-opened' : (isLast ? 'last-row' : '')} ${errorField?.systematic?.objective?.name === data.systematicObjectiveGridName ? 'invalid-tr' : ''}`}>
      <td className={`no-sub ${savedSystematicObjective?.skipped ? 'text-strike opacity-half' : ''}`}>
        <LineClamp text={data.systematicObjectiveGridName} />
      </td>
      {
        maxSkippedCriteria > 0 && <td>
          {
            !readOnlyMode && (savedSystematicObjective?.skipped ? <Button variant="ghost" size="inline" icon="bi-arrow-counterclockwise" data-tooltip-id="state-employee-undo-skip-criteria-tooltip" onClick={e => handleSetSkipped(e, false)} data-testid="undo-skip-criteria-button" /> :
              (
                maxSkippedCriteriaFull ? <></> :
                  <Button variant="ghost" size="inline" icon="bi-ban" data-tooltip-id="state-employee-skip-criteria-tooltip" onClick={e => handleSetSkipped(e, true)} data-testid="skip-criteria-button" />
              ))
          }
        </td>
      }
      <td className={`text-center v-middle ${savedSystematicObjective?.skipped ? 'opacity-half' : ''}`}><Button variant="ghost" size="inline" icon={`bi-bold bi-chevron-${isDetailOpened ? 'up' : 'down'}`} onClick={() => setIsDetailOpened(!isDetailOpened)} data-testid="toggle-detail" /></td>
      {
        levels.map((item, i) => <td key={i} className={`text-center col-radio ${savedSystematicObjective?.skipped ? 'opacity-half' : ''}`}>
          <input type="radio" className="mx-auto" name={data.systematicObjectiveGridName} value={item.level} disabled={readOnlyMode || savedSystematicObjective?.skipped} onChange={(e) => handleLevelChange(e, { name: data.systematicObjectiveGridName })} checked={item.level == savedSystematicObjective?.level} data-testid={`systematic-objective-level-${item.level}`} />
        </td>)
      }
      <td className={`v2-comment-td text-center w-1px`}>
        {
          readOnlyMode ? <ViewCommentIcon onClick={() => setShowComment(!showComment)} className="comment-icon cursor-pointer" /> : (
            isMandatory && emptyHtml(savedSystematicObjective?.comment) ? <Button variant="ghost" size="inline" icon="bi-exclamation-circle-fill" data-tooltip-id="extreme-comment-tooltip" onClick={() => setShowComment(!showComment)} data-testid="comment-toggle" /> :
              <>
                {
                  emptyHtml(savedSystematicObjective?.comment) ? <AddCommentIcon onClick={() => setShowComment(!showComment)} className="comment-icon cursor-pointer" data-tooltip-id="leave-comment-tooltip" />
                    : <EditCommentIcon onClick={() => setShowComment(!showComment)} className="comment-icon cursor-pointer" data-tooltip-id="leave-comment-tooltip" />
                }
              </>
          )
        }
      </td>
    </tr>
    {
      isDetailOpened && <tr className={`${!showComment && isLast ? 'last-row' : ''} ${errorField?.systematic?.objective?.name === data.systematicObjectiveGridName ? 'invalid-tr sub-tr' : ''}`}>
        <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className={`no-border-top ${isLast && !showComment ? '' : 'no-border-bottom'} description-td`}>
          <div className="d-flex">
            <i className="bi bi-bold bi-info-circle"></i>
            <ul>
              {getDescription()}
            </ul>
          </div>
        </td>
      </tr>
    }
    {
      showComment && <tr className={`${isLast ? 'last-row' : ''} ${errorField?.systematic?.objective?.name === data.systematicObjectiveGridName ? 'invalid-tr sub-tr' : ''}`}>
        <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className={`no-border-top ${isLast ? '' : 'no-border-bottom'} pt-1`}>
          <V3LevelComment showComment={showComment} setShowComment={setShowComment} data={savedSystematicObjective} readOnlyMode={readOnlyMode} handleSave={v => handleLevelChange(v, savedSystematicObjective, 'comment')} />
        </td>
      </tr>
    }
  </>
}

function V3LevelComment({ showComment, setShowComment, data, readOnlyMode, handleSave }) {
  const [comment, setComment] = useState(data?.comment || null);
  const maxLength = 600;

  useEffect(() => {
    // skip the mount run and read-only views, so opening the editor does not count as an edit
    if (readOnlyMode || comment === (data?.comment || null)) return;
    // check the length here: the editor's validate callback only lands one render later
    if ((getTextFromHtml(sanitize(comment))?.length || 0) > maxLength) return;
    handleSave(comment);
  }, [comment]);

  return <>
    {
      showComment && <div className={`v2-level-comment-div ${readOnlyMode ? '' : 'quill-with-shadow'} no-border-left position-relative`}>
        <EditorInput data-testid="comment-input"
          className={`${readOnlyMode ? 'hide-toolbar' : ''}`}
          value={comment}
          onChangeCallback={(v) => setComment(v)}
          readOnly={readOnlyMode}
          showCount={true}
          maxLength={maxLength} />
      </div>
    }
  </>
}

function StateEmployeeReviewerTr({ sortedLevels, type = 'first_evaluator', readOnlyMode, settings, extremeLevels, balancedScorecardDetails, handleReviewerLevelChange, handleReviewerCommentChange, isLastRow, isSecondaryEvaluator, allObjectivesChecked = false, balancedScorecardMetas = {}, isAdditionalJustificationRequired = false, maxSkippedCriteria = 0, compactMode = false, errorField = null }) {
  const { translate, editableTranslate } = useTranslation();

  const [showComment, setShowComment] = useState(false);

  const totalSystematic = useMemo(() => {
    if (type === 'first_evaluator') {
      return balancedScorecardDetails?.agreement_data?.totalValue?.systematic || -1;
    }
    return -1;
  }, [type, balancedScorecardDetails]);

  const isMandatory = useMemo(() => {
    const lvl = balancedScorecardDetails?.agreement_data?.overall_result?.[type]?.level;
    const lvlVal = balancedScorecardDetails?.agreement_data?.overall_result?.[type]?.value;
    const firstLvl = balancedScorecardDetails?.agreement_data?.overall_result?.first_evaluator?.level;

    let parsedTotalSystematic = totalSystematic; // only for first evaluator
    if (typeof parsedTotalSystematic === 'string') {
      parsedTotalSystematic = parseFloat(formatNumberTo(totalSystematic, '.'));
    }

    return settings?.systematicMandatoryCommentForExtremeRating === true
      && settings?.systematicMandatoryCommentForExtremeRatingType === 'based_on_single_objective'
      && (
        (lvl && extremeLevels.systematic.includes(lvl))
        || (balancedScorecardMetas?.balanced_scorecard_suggested_levels_type === 'half_step' && parsedTotalSystematic % 1 === 0.5)
        || (type === 'first_evaluator' && lvlVal && parseFloat(formatNumberTo(lvlVal, '.')) !== Math.round(parsedTotalSystematic))
        || (type === 'first_evaluator' && isAdditionalJustificationRequired)
        || (type === 'second_evaluator' && lvl && lvl !== firstLvl)); // LIX-1268
  }, [settings, balancedScorecardDetails, type, extremeLevels, totalSystematic, isAdditionalJustificationRequired]);

  const isInSuggestionRange = (level, total) => {
    // if (!allObjectivesChecked) return false;

    const floatLevel = parseFloat(formatNumberTo(level, '.'));
    const floatTotal = parseFloat(typeof total === 'string' ? formatNumberTo(total, '.') : total);
    const floorTotal = Math.floor(floatTotal);
    const ceilTotal = Math.ceil(floatTotal);

    if (balancedScorecardMetas?.balanced_scorecard_suggested_levels_type === 'half_step') {
      // LIX-1164 only suggest 2 levels for *.5 total
      if (floatTotal % 1 === 0.5) {
        if (floatLevel >= floorTotal && floatLevel <= ceilTotal) return true;
      } else {
        if (floatLevel % 1 !== 0.5 && Math.abs(floatTotal - floatLevel) < 0.5) return true;
      }
    } else if (balancedScorecardMetas?.balanced_scorecard_suggested_levels_type === 'default') {
      if (floatLevel >= floorTotal && floatLevel <= ceilTotal) return true;
    }

    return false;
  };

  return <>
    <tr className={`no-border-top ${showComment ? 'has-comment-opened' : (isLastRow ? 'last-row' : '')} ${errorField?.[type] ? 'invalid-tr' : ''}`}>
      <td colSpan={2}>{editableTranslate(type === 'first_evaluator' ? 'state_employee.overall_result_first' : 'state_employee.overall_result_second')}</td>
      {
        maxSkippedCriteria > 0 && <td></td>
      }
      {
        sortedLevels.map((item, i) => {
          const hightlight = isInSuggestionRange(item.value, totalSystematic) && !isSecondaryEvaluator && !readOnlyMode;

          return <td key={i} className={`text-center col-radio ${hightlight ? 'highlight-cell' : ''}`} data-tooltip-id={hightlight && item?.description !== 'sub' ? 'suggested-levels-tooltip' : ''}>
            <input type="radio" className={`mx-auto ${item?.description === 'sub' ? 'invisible' : ''}`} value={item.level} disabled={readOnlyMode} onChange={(e) => handleReviewerLevelChange(e, item.value, type)} checked={item.level == balancedScorecardDetails?.agreement_data?.overall_result?.[type]?.level} data-testid={`overall-result-${type}-level-${item.level}`} />
          </td>
        })
      }
      <td className="v2-comment-td">
        {
          readOnlyMode ? <ViewCommentIcon onClick={() => setShowComment(!showComment)} className={`comment-icon cursor-pointer ${compactMode ? 'invisible' : ''}`} /> :
            (isMandatory && emptyHtml(balancedScorecardDetails?.agreement_data?.overall_result?.[type]?.comment) ? <Button variant="ghost" size="inline" icon="bi-exclamation-circle-fill" data-tooltip-id={(type === 'first_evaluator' && isAdditionalJustificationRequired) ? `state_employee_additional_justification_tooltip` : `extreme-comment-tooltip`} onClick={() => setShowComment(!showComment)} data-testid={`comment-toggle-${type}`} /> :
              <>
                {
                  emptyHtml(balancedScorecardDetails?.agreement_data?.overall_result?.[type]?.comment) ? <AddCommentIcon onClick={() => setShowComment(!showComment)} className={`comment-icon cursor-pointer ${compactMode ? 'invisible' : ''}`} data-tooltip-id="leave-comment-tooltip" />
                    : <EditCommentIcon onClick={() => setShowComment(!showComment)} className="comment-icon cursor-pointer" data-tooltip-id="leave-comment-tooltip" />
                }
              </>
            )
        }
      </td>
    </tr>
    {
      showComment && <tr className={`${isLastRow ? 'last-row' : ''} ${errorField?.[type] ? 'invalid-tr sub-tr' : ''}`}>
        <td colSpan={sortedLevels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className={`no-border-top pt-1`}>
          <V3LevelComment showComment={showComment} setShowComment={setShowComment} data={balancedScorecardDetails?.agreement_data?.overall_result?.[type]} readOnlyMode={readOnlyMode} handleSave={v => handleReviewerCommentChange(v, type)} />
        </td>
      </tr>
    }
  </>;
}

function OtherEvaluations({ emptyColSpan, employee }) {
  const { translate, editableTranslate } = useTranslation();

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);

  const [evaluations, setEvaluations] = useState(null);

  useEffect(() => {
    if (employee?.hash_id) fetchEmployeeEvaluations();
  }, [employee]);

  const fetchEmployeeEvaluations = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data, status } = await api.get(`/employee-balanced-scorecards/employee/${employee?.hash_id}`);
      if (status === 200) {
        setEvaluations(data.data.filter(d => !d?.balanced_scorecard_setting?.slug?.includes('regelbeurteilung') && d?.evaluation_completed_at));
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch employee evaluations'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  const handlePdfReport = async (id) => {
    setError(null);
    setPdfLoadingId(id);

    const config = { responseType: 'blob' };

    try {
      const response = await api.post('/exports/employee-balanced-scorecard', {
        balanced_scorecard_hash_id: id,
        full: false,
        email: false
      }, config);

      fileDownloadResponse(response, 'evaluation-report.pdf');

      setPdfLoadingId(null);
    } catch (err) {
      let errObj = { message: 'Something went wrong.' };

      // if blob has error
      if (err.response && err.response.data instanceof Blob) {
        try {
          console.log('trying to parse blob error');
          const text = await err.response.data.text();
          errObj = JSON.parse(text);
        } catch (e) {
          // ignore
        }
      } else if (err.response && err.response.data) {
        errObj = err.response.data;
      }

      setPdfLoadingId(null);
      setError(errorMessage(errObj, 'generate employee evaluation PDF report'));
      Sentry.captureException(errObj);
    }
  };

  return <Fragment>
    {
      (evaluations && evaluations?.length > 0) && <tr>
        <td colSpan={emptyColSpan} className="no-border-all p-3"></td>
      </tr>
    }
    {
      error && <tr>
        <td colSpan={emptyColSpan} className="no-border-all px-0">
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </td>
      </tr>
    }
    {
      loading && <tr>
        <td colSpan={emptyColSpan} className="no-border-all p-3 text-center">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </td>
      </tr>
    }
    {
      (evaluations && evaluations?.length > 0) && <tr>
        <td colSpan={emptyColSpan} className="no-border-all p-0">
          <h5 className="mb-0">{editableTranslate('state_employee.other_evaluations', 'Andere Beurteilungen')}</h5>
        </td>
      </tr>
    }
    {
      (evaluations && evaluations?.length > 0) && evaluations?.map((evaluation, i) => {
        const nextEvaluation = evaluations[i + 1];

        if (i % 2 === 0) {
          return <tr key={i} data-testid={`row-${evaluation?.hash_id}`}>
            <td colSpan={emptyColSpan} className="no-border-all px-0 pt-2 pb-2">
              <div className="row mx-0">
                <div className="col-6 pl-0">
                  <div className="d-flex justify-content-between">
                    <div>
                      <span>
                        <SettingName setting={evaluation?.balanced_scorecard_setting} className="font-weight-bold v-middle" />
                        <span className="badge badge-pill badge-evaluator font-weight-normal text-normal v-middle ml-2 max-w-200 use-ellipsis">{evaluation?.group?.name}</span>
                      </span>
                      <span className="d-block mt-1">
                        <span className="v-middle">{editableTranslate('field.result')}: {evaluation?.result_formatted}</span>
                        <Button variant="secondary" size="sm" icon="bi-download" onClick={() => handlePdfReport(evaluation?.hash_id)} loading={pdfLoadingId === evaluation?.hash_id} disabled={!!pdfLoadingId} data-tooltip-id="download-report-tooltip" data-testid="download-button" className="v-middle text-normal ml-2" />
                      </span>
                    </div>
                    <div>
                    </div>
                  </div>
                </div>
                {
                  nextEvaluation && <div className="col-6 pr-0">
                    <div className="d-flex justify-content-between">
                      <div>
                        <span>
                          <SettingName setting={nextEvaluation?.balanced_scorecard_setting} className="font-weight-bold v-middle" />
                          <span className="badge badge-pill badge-evaluator font-weight-normal text-normal v-middle ml-2 max-w-200 use-ellipsis">{nextEvaluation?.group?.name}</span>
                        </span>
                        <span className="d-block mt-1">
                          <span className="v-middle">{editableTranslate('field.result')}: {nextEvaluation?.result_formatted}</span>
                          <Button variant="secondary" size="sm" icon="bi-download" onClick={() => handlePdfReport(nextEvaluation?.hash_id)} loading={pdfLoadingId === nextEvaluation?.hash_id} disabled={!!pdfLoadingId} data-tooltip-id="download-report-tooltip" data-testid="download-button" className="v-middle text-normal ml-2" />
                        </span>
                      </div>
                      <div>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </td>
          </tr>;
        }
        return null;
      })
    }
  </Fragment>
}

function V3SystematicObjective({ settings, balancedScorecardDetails, readOnlyMode, levels, countSystematic, extremeLevels, saveAgreement, balancedScorecardMetas, evaluationDirty, setEvaluationDirty, stateEvaluationFlow = {}, hideObjectives = false, previewMode = null, handleChangePreviewMode = null, handleBtnClickBlur = null, handleCompleteEvaluation = null, completeLoading = false, validateAll = null,
  reviewerView = false,
  interviewEnabled = false,
  interviews = [],
  canOpenInterviewTab = false,
  fetchBalancedScorecards = null,
  fetchBalancedScorecardDetails = null,
  setInterviewDirty = null,
  interviewsData = [],
  fetchInterviews = null,
  complaints = [],
  fetchComplaints = null,
  stateCompleteEvaluationButton = <></>,
  errorField = null,
  setErrorField = null
}) {
  const { translate, editableTranslate } = useTranslation();

  const stateEmployeeSettings = useSelector(getStateEmployeeSettings);

  const generalSettings = useSelector(getGeneralSettings);

  const location = useLocation();

  const settingIdentifier = useMemo(() => {
    return balancedScorecardDetails?.balanced_scorecard_setting?.slug;
  }, [balancedScorecardDetails]);

  const settingCustomConfig = useMemo(() => {
    return balancedScorecardDetails?.balanced_scorecard_setting?.custom_config;
  }, [balancedScorecardDetails]);

  const initialEvaluationEnabled = useMemo(() => {
    return settingCustomConfig?.initial_evaluation_enabled ?? true;
  }, [settingCustomConfig]);

  const secondaryEvaluationEnabled = useMemo(() => {
    let enabled = settingCustomConfig?.secondary_evaluation_enabled ?? true;
    if (stateEvaluationFlow?.flow === 'primary_evaluator_only') {
      enabled = false;
    }
    return enabled;
  }, [settingCustomConfig, stateEvaluationFlow]);

  const isSecondaryEvaluator = useMemo(() => {
    return location.pathname.includes('/secondary-');
  }, [location]);

  const resultAsNumeric = useMemo(() => {
    if (!generalSettings && !settingCustomConfig) return false;

    if (settingCustomConfig?.result_format === 'numeric') return true;

    // generalSettings is an array of object, search the key 'balanced_scorecard_result_format'
    const setting = generalSettings?.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }

    return false;
  }, [generalSettings, settingCustomConfig]);

  const maxSkippedCriteria = useMemo(() => {
    if (stateEmployeeSettings && !empty(stateEmployeeSettings?.state_employee_max_skipped_criteria) && !isSecondaryEvaluator) {
      return +stateEmployeeSettings?.state_employee_max_skipped_criteria;
    }

    return 0;
  }, [stateEmployeeSettings, isSecondaryEvaluator]);

  const initialEvaluationCompleted = useMemo(() => {
    return balancedScorecardDetails?.initial_evaluation_completed_at;
  }, [balancedScorecardDetails]);

  const secondaryEvaluationCompleted = useMemo(() => {
    return balancedScorecardDetails?.evaluation_completed_at;
  }, [balancedScorecardDetails]);

  const previousResults = useMemo(() => {
    // previous_evaluation_results = {
    //         "period": "2026",
    //         "regular_evaluation": {
    //             "slug": "regelbeurteilung",
    //             "uuid": "f24dbe3c-e68a-415d-aa2f-d4433ac5e884",
    //             "result": "5.00"
    //         }
    //     }
    return balancedScorecardDetails?.employee.previous_evaluation_results || {};
  }, [balancedScorecardDetails]);

  const getLabeledValue = (value) => {
    if (empty(value)) return '-';

    let tempValue = value;
    if (typeof tempValue === 'string') tempValue = parseFloat(formatNumberTo(tempValue, '.'));

    if (resultAsNumeric) return formatNumberTo(tempValue);

    for (let i = levels.length - 1; i >= 0; i--) {
      const lvl = levels[i].level;
      const val = parseFloat(formatNumberTo(levels[i].value, '.'));
      if (val % 1 === 0.5) {
        if (tempValue === val) return lvl?.replace('/', ' / ');
      } else if (Math.abs(val - tempValue) < 0.5) return lvl?.replace('/', ' / ');
    }

    return '-';
  };

  const formattedPreviousResult = useMemo(() => {
    const result = previousResults?.regular_evaluation?.result;
    if (empty(result)) return null;
    const labeledValue = getLabeledValue(result);
    if (labeledValue === '-') return null;
    return labeledValue;
  }, [previousResults, levels, resultAsNumeric]);

  const [isHeaderOpened, setIsHeaderOpened] = useState(false);

  const [tempDetails, setTempDetails] = useState(null);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTempDetails(structuredClone(balancedScorecardDetails));
    setEvaluationDirty(false);
  }, [balancedScorecardDetails]);

  useEffect(() => () => setEvaluationDirty(false), []);

  const maxSkippedCriteriaFull = useMemo(() => {
    if (maxSkippedCriteria > 0 && tempDetails) {
      const filtered = tempDetails?.agreement_data?.systematicObjectives?.filter(o => o?.skipped);
      return filtered.length >= maxSkippedCriteria;
    }
    return false;
  }, [maxSkippedCriteria, tempDetails]);

  const allObjectivesChecked = useMemo(() => {
    if (tempDetails?.agreement_data?.systematicObjectives && Array.isArray(tempDetails?.agreement_data?.systematicObjectives)) {
      const filtered = tempDetails.agreement_data.systematicObjectives.filter(o => !empty(o?.value) || !empty(o?.level));

      if (filtered.length === settings?.systematicObjectiveDatagrid?.length) {
        return true;
      }
    }
    return false;
  }, [tempDetails, settings]);

  const labeledTotalValue = useMemo(() => {
    const tempTotalValue = tempDetails?.agreement_data?.totalValue?.systematic || 0;
    return getLabeledValue(tempTotalValue);
  }, [tempDetails, levels, resultAsNumeric]);

  const isAdditionalJustificationRequired = useMemo(() => {
    let tempResultLevel = tempDetails?.agreement_data?.overall_result?.first_evaluator?.level;
    if (resultAsNumeric) {
      tempResultLevel = tempDetails?.agreement_data?.overall_result?.first_evaluator?.value;
    }

    if (!formattedPreviousResult || !tempResultLevel) return false;
    return formattedPreviousResult !== tempResultLevel;
  }, [formattedPreviousResult, tempDetails, resultAsNumeric]);

  const evaluatorCanSave = useMemo(() => {
    if (!isSecondaryEvaluator) {
      return !initialEvaluationCompleted && (stateEvaluationFlow?.flow ? stateEvaluationFlow?.current_step === 'primary_evaluation' : true);
    }

    return !secondaryEvaluationCompleted && (stateEvaluationFlow?.flow ? stateEvaluationFlow?.current_step === 'secondary_evaluation' : true);
  }, [isSecondaryEvaluator, initialEvaluationCompleted, secondaryEvaluationCompleted, stateEvaluationFlow]);

  const handleLevelChange = async (e, reference, key = 'level') => {
    if (!tempDetails) return;

    let val = e;
    if (key === 'level') {
      val = e.target.value;

      if (e.target.type === 'checkbox' && !e.target.checked) {
        val = null;
      }
    }

    let agreementData = tempDetails?.agreement_data;
    if (!agreementData) agreementData = {};

    let totalValue = 0;

    const parentName = reference?.parent || reference.name;

    let countSkipped = 0;
    for (let i = 0; i < agreementData.systematicObjectives.length; i++) {
      if (agreementData.systematicObjectives[i].name === parentName) {
        agreementData.systematicObjectives[i][key] = val;

      }

      if (agreementData.systematicObjectives[i]?.skipped) {
        agreementData.systematicObjectives[i].level = null;
        countSkipped++;
      }

      if (agreementData.systematicObjectives[i]?.level) {
        const levelObj = settings?.systematicObjectivePerformanceLevelGrid?.find(obj => obj.systematicObjectiveLevel === agreementData.systematicObjectives[i]?.level);
        if (levelObj) totalValue += parseFloat(formatNumberTo(levelObj.systematicObjectiveValue, '.'));
      }
    }

    if (!agreementData?.totalValue) {
      agreementData.totalValue = {};
    }
    agreementData.totalValue.systematic = totalValue / (agreementData.systematicObjectives.length - countSkipped);

    const newTempDetails = structuredClone(tempDetails);
    newTempDetails.agreement_data = agreementData;
    setTempDetails(newTempDetails);
    setEvaluationDirty(true);

    if (errorField) {
      validateAll(newTempDetails);
    }
  };

  const handleReviewerLevelChange = async (e, val, type) => {
    if (!tempDetails) return;

    let agreementData = tempDetails.agreement_data;
    if (!agreementData) agreementData = {};

    if (!agreementData?.overall_result) agreementData.overall_result = {
      first_evaluator: { level: null, value: null, comment: null },
      second_evaluator: { level: null, value: null, comment: null }
    };

    let level = e.target.value;
    let value = val;
    if (e.target.type === 'checkbox' && !e.target.checked) {
      level = null;
      value = null;
    }

    agreementData.overall_result[type].level = level;
    agreementData.overall_result[type].value = value;

    const newTempDetails = structuredClone(tempDetails);
    newTempDetails.agreement_data = agreementData;
    setTempDetails(newTempDetails);
    setEvaluationDirty(true);

    if (errorField) {
      validateAll(newTempDetails);
    }
  };

  const handleReviewerCommentChange = async (val, type) => {
    if (!tempDetails) return;

    let agreementData = tempDetails.agreement_data;
    if (!agreementData) agreementData = {};

    if (!agreementData?.overall_result) agreementData.overall_result = {
      first_evaluator: { level: null, value: null, comment: null },
      second_evaluator: { level: null, value: null, comment: null }
    };

    agreementData.overall_result[type].comment = val;

    const newTempDetails = structuredClone(tempDetails);
    newTempDetails.agreement_data = agreementData;
    setTempDetails(newTempDetails);
    setEvaluationDirty(true);

    if (errorField) {
      validateAll(newTempDetails);
    }
  };

  const handleAdditionalChange = (val, key, parentKey = null) => {
    if (!tempDetails) return;

    let agreementData = tempDetails.agreement_data;
    if (!agreementData) agreementData = {};

    if (parentKey) {
      if (!agreementData[parentKey]) agreementData[parentKey] = {};
      agreementData[parentKey][key] = val;
    } else {
      agreementData[key] = val;
    }

    const newTempDetails = structuredClone(tempDetails);
    newTempDetails.agreement_data = agreementData;
    setTempDetails(newTempDetails);
    setEvaluationDirty(true);
  };

  const handleSaveAll = async (showToast = true, callback = null) => {
    setLoading(true);
    await saveAgreement(tempDetails.agreement_data, showToast, callback);
    if (!callback) setErrorField(null);
    setLoading(false);
  };

  const handleProceedEvaluation = async () => {
    await handleSaveAll(false, (bsData) => handleCompleteEvaluation(null, false, bsData));
  };

  return <div className={`${hideObjectives ? '' : 'mt-4'} custom-systematic-state-employee-div ${''}`}>
    {
      !hideObjectives && <span className="h5">{editableTranslate('state_employee.individual_objectives', 'Einzelmerkmale')}</span>
    }
    <div className="table-responsive">
      <table className="table table-borderless" data-testid="systematic-objective-datagrid-table">
        {
          !hideObjectives && <thead>
            <tr className="custom-th">
              <th className={''}></th>
              {
                maxSkippedCriteria > 0 && <th className="text-center w-1px"></th>
              }
              <th className="text-center"><Button variant="ghost" size="inline" icon={`bi-bold bi-chevron-${isHeaderOpened ? 'up' : 'down'}`} onClick={() => setIsHeaderOpened(!isHeaderOpened)} data-testid="toggle-header" /></th>
              {
                levels.map((item, i) => <th key={i} className={`text-center col-radio`}>{item?.description === 'sub' ? '' : item.level}</th>)
              }
              <th className="text-center w-1px"></th>
            </tr>
          </thead>
        }
        <tbody>
          {
            !hideObjectives && <Fragment>
              {
                isHeaderOpened && <tr>
                  <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-y header-description-td">
                    <div className="header-description-div">
                      <span className="font-weight-bold text-big">{editableTranslate('state_employee.rating_explanatory', 'Beurteilungsskala')}:</span>
                      <div className="row mt-2">
                        {
                          levels.filter(l => l?.description !== 'sub').map((item, i) => <div key={i} className="col-6 mb-2"><strong>{item.level}</strong>: {editableTranslate('state_employee.rating_' + item.level + '_description')}</div>)
                        }
                      </div>
                    </div>
                  </td>
                </tr>
              }
              {
                settings?.systematicObjectiveDatagrid && settings?.systematicObjectiveDatagrid.map((item, i) => <V3SystematicObectiveRow key={i} isLast={settings?.systematicObjectiveDatagrid.length === (i + 1)} data={item} settings={settings} balancedScorecardDetails={tempDetails} handleLevelChange={handleLevelChange} readOnlyMode={readOnlyMode || isSecondaryEvaluator || (!isSecondaryEvaluator && initialEvaluationCompleted)} levels={levels} extremeLevels={extremeLevels} maxSkippedCriteria={maxSkippedCriteria} maxSkippedCriteriaFull={maxSkippedCriteriaFull} errorField={errorField} />)
              }
              {
                countSystematic === 0 && <tr className="no-height">
                  <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="text-center">
                    {editableTranslate('evaluation.no_systematic_objectives_in_lk')}
                  </td>
                </tr>
              }
              <tr>
                <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-all p-2"></td>
              </tr>
              {
                labeledTotalValue !== '-' && <tr className="result-tr">
                  <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 3 : 2)} className="no-border-all pr-0">
                    <div className="text-right font-weight-bold text-big">{editableTranslate('state_employee.result_individual', 'Durchschnittsbewertung')}:</div>
                  </td>
                  <td className="font-weight-bold no-border-all v-top text-big text-nowrap">
                    {labeledTotalValue}
                  </td>
                </tr>
              }
            </Fragment>
          }
          {
            (initialEvaluationEnabled || secondaryEvaluationEnabled) && <Fragment>
              {
                !hideObjectives && <tr>
                  <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-all p-3"></td>
                </tr>
              }
              <tr>
                <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className={`px-0 pt-0 ${hideObjectives ? 'pb-2' : 'pb-3'} no-border-all`}>
                  <span className={`h5`}>{editableTranslate('state_employee.overall_result', 'Gesamtergebnis')}</span>
                </td>
              </tr>
              <tr className="custom-th">
                <th colSpan={2}></th>
                {
                  maxSkippedCriteria > 0 && <th className="text-center w-1px"></th>
                }
                {
                  levels.map((item, i) => <th key={i} className={`text-center col-radio`}>{item?.description === 'sub' ? '' : item.level}</th>)
                }
                <th className="text-center w-1px"></th>
              </tr>
              {
                initialEvaluationEnabled && <StateEmployeeReviewerTr sortedLevels={levels} type="first_evaluator" readOnlyMode={readOnlyMode || isSecondaryEvaluator || (!isSecondaryEvaluator && initialEvaluationCompleted) || hideObjectives} settings={settings} extremeLevels={extremeLevels} balancedScorecardDetails={tempDetails} handleReviewerLevelChange={handleReviewerLevelChange} handleReviewerCommentChange={handleReviewerCommentChange} isLastRow={!secondaryEvaluationEnabled || (!isSecondaryEvaluator && !secondaryEvaluationCompleted)} isSecondaryEvaluator={isSecondaryEvaluator} allObjectivesChecked={allObjectivesChecked} balancedScorecardMetas={balancedScorecardMetas} isAdditionalJustificationRequired={isAdditionalJustificationRequired} maxSkippedCriteria={maxSkippedCriteria} compactMode={true} errorField={errorField} />
              }
              {
                (secondaryEvaluationEnabled && (isSecondaryEvaluator || secondaryEvaluationCompleted)) && <StateEmployeeReviewerTr sortedLevels={levels} type="second_evaluator" readOnlyMode={readOnlyMode || !isSecondaryEvaluator || (isSecondaryEvaluator && (!initialEvaluationCompleted || stateEvaluationFlow?.current_step !== 'secondary_evaluation'))} settings={settings} extremeLevels={extremeLevels} balancedScorecardDetails={tempDetails} handleReviewerLevelChange={handleReviewerLevelChange} handleReviewerCommentChange={handleReviewerCommentChange} isLastRow={true} isSecondaryEvaluator={isSecondaryEvaluator} maxSkippedCriteria={maxSkippedCriteria} compactMode={true} errorField={errorField} />
              }
              <tr>
                <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-all p-3"></td>
              </tr>
              {
                !settingIdentifier.includes('beurteilungsbeitrag') && <tr>
                  {
                    (previousResults?.period || formattedPreviousResult) && <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-all p-0">
                      <div className="">
                        <span className={`h5`}>{editableTranslate('state_employee.previous_results', 'Vorherige Ergebnisse')}</span>
                        <div className="d-flex flex-wrap mt-2">
                          {
                            previousResults?.period && <div className="mr-4">
                              <span className="font-weight-bold">{editableTranslate('state_employee.period', 'Zeitraum')}:</span> {previousResults.period}
                            </div>
                          }
                          {
                            formattedPreviousResult && <div>
                              <span className="font-weight-bold">{editableTranslate('state_employee.regular_evaluation', 'Regelbeurteilung')}:</span> {formattedPreviousResult}
                            </div>
                          }
                        </div>
                      </div>
                    </td>
                  }
                </tr>
              }
              {
                settingIdentifier.includes('regelbeurteilung') && <OtherEvaluations
                  emptyColSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)}
                  employee={balancedScorecardDetails?.employee}
                />
              }
            </Fragment>
          }
          <tr>
            <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-all p-3"></td>
          </tr>
          <tr>
            <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="px-0 pt-0 pb-2 no-border-all">
              <span className={`h5`}>{editableTranslate('state_employee.additional_information', 'Zusätzliche Angaben')}</span>
            </td>
          </tr>
          <tr>
            <td colSpan={levels.length + (maxSkippedCriteria > 0 ? 4 : 3)} className="no-border-all p-0">
              <div className="row mx-0">
                <div className="col-12 col-lg-6 p-0 pr-lg-3 pl-lg-0 mb-3 mb-lg-0">
                  <div className="mb-2 font-weight-bold">
                    {editableTranslate('state_employee.skills_and_suitability_assessment', 'Befähigungseinschätzung und Eignungsaussage')}
                    <i className="bi bi-info-circle ml-1" data-tooltip-id={'state_employee_skills_and_suitability_assessment_tooltip'}></i>
                  </div>
                  {
                    hideObjectives ? <div dangerouslySetInnerHTML={{ __html: sanitize(tempDetails?.agreement_data?.additional_information?.skills_and_suitability_assessment || "-") }} /> :
                      <EditorInput data-testid="skills-and-suitability-assessment-input"
                        className={`max-height-200 ${readOnlyMode ? 'hide-toolbar' : ''}`}
                        maxLength={1000}
                        showCount={true}
                        value={tempDetails?.agreement_data?.additional_information?.skills_and_suitability_assessment}
                        onChangeCallback={(v) => handleAdditionalChange(v, 'skills_and_suitability_assessment', 'additional_information')}
                        readOnly={readOnlyMode} />
                  }
                </div>
                <div className="col-12 col-lg-6 p-0 pl-lg-3 pr-lg-0">
                  <div className="mb-2 font-weight-bold">
                    {editableTranslate('state_employee.promotion_proposal', 'Förderungsvorschlag')}
                    <i className="bi bi-info-circle ml-1" data-tooltip-id={'state_employee_promotion_proposal_tooltip'}></i>
                  </div>
                  {
                    hideObjectives ? <div dangerouslySetInnerHTML={{ __html: sanitize(tempDetails?.agreement_data?.additional_information?.promotion_proposal || "-") }} /> :
                      <EditorInput data-testid="promotion-proposal-input"
                        className={`max-height-200 ${readOnlyMode ? 'hide-toolbar' : ''}`}
                        maxLength={1000}
                        showCount={true}
                        value={tempDetails?.agreement_data?.additional_information?.promotion_proposal}
                        onChangeCallback={(v) => handleAdditionalChange(v, 'promotion_proposal', 'additional_information')}
                        readOnly={readOnlyMode} />
                  }
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    {
      (!hideObjectives && !readOnlyMode && ((!isSecondaryEvaluator && !initialEvaluationCompleted) || (isSecondaryEvaluator && initialEvaluationCompleted && !secondaryEvaluationCompleted))) && <div className="text-right">
        <Button variant="secondary" onClick={handleSaveAll} loading={loading} data-testid="save-button">{editableTranslate('button.save')}</Button>

        {stateCompleteEvaluationButton}
      </div>
    }
    {
      hideObjectives && <>
        {
          (interviewEnabled && (interviews && interviews.length > 0)) && canOpenInterviewTab && <div className="mt-3 mb-5">
            <InterviewMode
              balancedScorecardDetails={balancedScorecardDetails}
              fetchBalancedScorecards={fetchBalancedScorecards}
              fetchBalancedScorecardDetails={fetchBalancedScorecardDetails}
              reviewerView={reviewerView}
              setInterviewDirty={setInterviewDirty}
              interviews={interviewsData}
              fetchInterviews={fetchInterviews}
              complaints={complaints}
              fetchComplaints={fetchComplaints}
              isSecondaryEvaluator={isSecondaryEvaluator}
              stateEvaluationFlow={stateEvaluationFlow}
              compactMode={true} />
          </div>
        }
        {
          previewMode !== 'complete_evaluation' && <div className="text-right">
            {
              (secondaryEvaluationCompleted || evaluatorCanSave) && <Button variant="secondary" onClick={(e) => handleBtnClickBlur(e, () => handleChangePreviewMode('complete_evaluation', false))} disabled={readOnlyMode || balancedScorecardDetails?.rating_mode === 'average_value_percentile' || secondaryEvaluationCompleted} data-tooltip-id={secondaryEvaluationCompleted ? 'state-employee-evaluation-completed' : ''} data-testid="evaluation-tab-button">
                <i className="bi bi-file-earmark-check"></i> {editableTranslate('balanced_scorecard.evaluation_tab_button')}
              </Button>
            }

            {
              secondaryEvaluationCompleted && <Button variant="secondary" onClick={(e) => handleBtnClickBlur(e, () => previewMode === 'report' ? handleChangePreviewMode('evaluation') : handleChangePreviewMode('report'))} disabled={!balancedScorecardDetails?.evaluation_completed_at || balancedScorecardDetails?.rating_mode === 'average_value_percentile'} data-testid="evaluation-report-button" className="ml-2">
                <i className="bi bi-file-text"></i> {editableTranslate('state_employee.evaluation_report_button')}
              </Button>
            }
          </div>
        }

        {
          previewMode === 'complete_evaluation' && <div>
            <div className="complete-evaluation-div">
              {
                !isSecondaryEvaluator && <>
                  <h5>{editableTranslate('state_employee.initial_complete_title')}</h5>
                  <ul>
                    <li>{editableTranslate('state_employee.initial_complete_list_1')}</li>
                    <li>{editableTranslate('state_employee.initial_complete_list_2')}</li>
                  </ul>
                </>
              }
              {
                isSecondaryEvaluator && <>
                  <h5>{editableTranslate('state_employee.secondary_complete_title')}</h5>
                  <ul>
                    <li>{editableTranslate('state_employee.secondary_complete_list_1')}</li>
                    <li>{editableTranslate('state_employee.secondary_complete_list_2')}</li>
                  </ul>
                </>
              }
              <div className="text-right">
                <Button variant="secondary" onClick={() => handleChangePreviewMode('evaluation')} loading={completeLoading} data-testid="cancel-button">{editableTranslate('button.cancel')}</Button>
                <Button variant="primary" className="ml-2" onClick={handleProceedEvaluation} loading={completeLoading} data-testid="proceed-button">
                  {editableTranslate('button.proceed')}
                </Button>
              </div>
            </div>
          </div>
        }
      </>
    }
    <Tooltip id="state_employee_additional_justification_tooltip" place="bottom" clickable={true}>
      <span dangerouslySetInnerHTML={{ __html: sanitize(translate('tooltips.state_employee.additional_justification_tooltip')) }} />
    </Tooltip>
    <Tooltip id="state_employee_skills_and_suitability_assessment_tooltip" place="bottom" clickable={true}>
      <span dangerouslySetInnerHTML={{ __html: sanitize(translate('tooltips.state_employee.skills_and_suitability_assessment_tooltip')) }} />
    </Tooltip>
    <Tooltip id="state_employee_promotion_proposal_tooltip" place="bottom" clickable={true} content={translate('tooltips.state_employee.promotion_proposal_tooltip')} />
    <Tooltip id="state-employee-skip-criteria-tooltip" place="left" globalCloseEvents={{ clickOutsideAnchor: true }} content={translate('tooltips.state_employee.skip_criteria_tooltip')} />
    <Tooltip id="state-employee-undo-skip-criteria-tooltip" place="left" globalCloseEvents={{ clickOutsideAnchor: true }} content={translate('tooltips.state_employee.undo_skip_criteria_tooltip')} />

    <Tooltip id="download-report-tooltip" place="bottom" content={translate('balanced_scorecard.report_download_tooltip')} />
  </div>
}

export default function Evaluation({ settings, balancedScorecardDetails, handleLevelChange, readOnlyMode, levels, countSystematic, extremeLevels, saveAgreement, balancedScorecardMetas, evaluationDirty, setEvaluationDirty, stateEvaluationFlow = {}, hideObjectives = false, previewMode = null, handleChangePreviewMode = null, handleBtnClickBlur = null, handleCompleteEvaluation = null, completeLoading = false, validateAll = null,
  reviewerView = false,
  interviewEnabled = false,
  interviews = [],
  canOpenInterviewTab = false,
  fetchBalancedScorecards = null,
  fetchBalancedScorecardDetails = null,
  setInterviewDirty = null,
  interviewsData = [],
  fetchInterviews = null,
  complaints = [],
  fetchComplaints = null,
  stateCompleteEvaluationButton = <></>,
  errorField = null,
  setErrorField = null,
}) {
  const { translate, editableTranslate } = useTranslation();

  return (
    <div>
      {
        settings?.showRatingNotes && settings?.ratingNotes &&
        <div className="feedback-div d-flex mt-4">
          <div className="mr-2">
            <img src={infoIcon} alt="info" />
          </div>
          <div className="w-100">
            <ExpandableHtml html={sanitize(settings?.ratingNotes)} className="fade-blue" />
          </div>
        </div>
      }

      <V3SystematicObjective
        settings={settings}
        balancedScorecardDetails={balancedScorecardDetails}
        handleLevelChange={handleLevelChange}
        readOnlyMode={readOnlyMode}
        levels={levels?.general ?? levels?.systematic}
        countSystematic={countSystematic}
        extremeLevels={extremeLevels}
        saveAgreement={saveAgreement}
        balancedScorecardMetas={balancedScorecardMetas}
        evaluationDirty={evaluationDirty}
        setEvaluationDirty={setEvaluationDirty}
        stateEvaluationFlow={stateEvaluationFlow}
        hideObjectives={hideObjectives}
        previewMode={previewMode}
        handleChangePreviewMode={handleChangePreviewMode}
        handleBtnClickBlur={handleBtnClickBlur}
        handleCompleteEvaluation={handleCompleteEvaluation}
        completeLoading={completeLoading}
        validateAll={validateAll}
        reviewerView={reviewerView}
        interviewEnabled={interviewEnabled}
        interviews={interviews}
        canOpenInterviewTab={canOpenInterviewTab}
        fetchBalancedScorecards={fetchBalancedScorecards}
        fetchBalancedScorecardDetails={fetchBalancedScorecardDetails}
        setInterviewDirty={setInterviewDirty}
        interviewsData={interviewsData}
        fetchInterviews={fetchInterviews}
        complaints={complaints}
        fetchComplaints={fetchComplaints}
        stateCompleteEvaluationButton={stateCompleteEvaluationButton}
        errorField={errorField}
        setErrorField={setErrorField} />

      <Tooltip id="suggested-levels-tooltip" place="bottom" content={translate('state_employee.suggested_levels')} />
    </div>
  );
}
